/**
 * Say something to a screen reader that the page shows without words.
 *
 * Some answers are confirmed by the page rearranging itself — a task handed
 * over moves to another group, a check-in answered takes its own question
 * away. That is the whole confirmation, and it is a silent one: there is no
 * text to read and nothing receives focus. A toast would carry it only by
 * accident, because its library puts one in a live region, and a toast for a
 * change the page already shows is noise; this says it on purpose, to the
 * screen reader alone.
 *
 * One region, reused. Assistive technology watches a node for changes, so a
 * region created per message is announced inconsistently — it has to be in
 * the document before the text lands in it.
 */
let region: HTMLElement | null = null;

const liveRegion = (): HTMLElement => {
  if (region?.isConnected) return region;
  region = document.createElement("div");
  // `polite` waits for a pause: none of these interrupt what the user is
  // doing, and all of them follow an action they just took.
  region.setAttribute("role", "status");
  region.setAttribute("aria-live", "polite");
  region.className = "sr-only";
  document.body.appendChild(region);
  return region;
};

// In the document from the start rather than from the first message:
// assistive technology announces a change to a node it already watches, and a
// region that appears in the same breath as its first words is read late or
// not at all. Guarded for anything that imports this without a document.
if (typeof document !== "undefined") liveRegion();

export const announce = (message: string): void => {
  const node = liveRegion();
  // Cleared first, and set on the next tick: screen readers announce a
  // *change* to the region, and writing the same words twice is not one —
  // answering two check-ins the same way would otherwise say it once.
  node.textContent = "";
  window.setTimeout(() => {
    node.textContent = message;
  }, 50);
};
